import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeFirebaseStorageUrl, proxifyFirebaseStorageImage } from '../utils/storageImageProxy';

const BROKEN_IMAGE_PLACEHOLDER = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="960" height="540" viewBox="0 0 960 540"><rect width="960" height="540" fill="#0f172a"/><rect x="18" y="18" width="924" height="504" rx="24" fill="#111827" stroke="#334155"/><path d="M240 360l140-140 100 100 120-120 120 160H240z" fill="#1e293b"/><circle cx="368" cy="200" r="44" fill="#334155"/><text x="480" y="430" fill="#94a3b8" font-family="Arial,sans-serif" font-size="26" text-anchor="middle">Image unavailable</text></svg>'
)}`;

const StorageImage = ({ src, onError, ...imgProps }) => {
  const rawSrc = useMemo(() => normalizeFirebaseStorageUrl(src), [src]);
  const proxiedSrc = useMemo(() => proxifyFirebaseStorageImage(rawSrc), [rawSrc]);
  const sourceChain = useMemo(() => {
    const ordered = [rawSrc, proxiedSrc, BROKEN_IMAGE_PLACEHOLDER];
    const seen = new Set();
    return ordered.filter((candidate) => {
      const value = String(candidate || '').trim();
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  }, [proxiedSrc, rawSrc]);
  const [sourceIndex, setSourceIndex] = useState(0);
  const activeSrc = sourceChain[sourceIndex] || '';
  const didCallOnErrorRef = useRef(false);

  useEffect(() => {
    setSourceIndex(0);
    didCallOnErrorRef.current = false;
  }, [sourceChain]);

  const handleError = useCallback((event) => {
    const isLastSource = sourceIndex >= sourceChain.length - 1;
    if (!isLastSource) {
      if (typeof window !== 'undefined' && String(activeSrc || '').startsWith('/api/storage-image')) {
        // If proxy fails, use direct Firebase URLs for subsequent renders in this session.
        window.__stocktagDisableStorageProxy = true;
      }
      setSourceIndex((prev) => Math.min(prev + 1, sourceChain.length - 1));
      return;
    }
    if (!didCallOnErrorRef.current) {
      didCallOnErrorRef.current = true;
      onError?.(event);
    }
  }, [activeSrc, onError, sourceChain, sourceIndex]);

  return (
    <img
      {...imgProps}
      src={activeSrc}
      onError={handleError}
    />
  );
};

export default StorageImage;
